import type { FormEvent } from 'react';
import ModalProductAiResponseData from '@/components/ModalProductAiResponseData';
import { BrandsTableData } from '@/components/brands-table/use-brands-table-view-model';
import {
    BrandGroupEditorBrand,
    BrandGroupEditorData,
    ProductGroupEditorData,
    ProductGroupEditorProduct,
} from '@/components/group-editor/group-editor-domain-hooks';
import { ProductsTableData } from '@/components/products-table/use-products-table-view-model';
import { ProductAiAnalysisEntity } from '@/entities/product-ai-analysis.entity';
import { ProductAnalysisEntity } from '@/entities/product-analysis.entity';
import { GroupTableModalsCore } from '@/components/group-tables/GroupTableModalsCore';
import { TableCheckboxData, TableColumn } from '@/ui/Table';
import Modal from '@/ui/Modal';
import type { ProductPhotoEntity } from '@/entities/product.entity';

export type BrandsTableModalsProps = {
    openedBrandGroup: BrandsTableData | null;
    openedBrandGroupAnalysis: ProductAnalysisEntity | null;
    isBrandGroupAnalysisLoading: boolean;
    brandGroupAnalysisError: string | null;
    onBrandGroupAnalysisClose: () => void;
    openedGroupEditor: BrandsTableData | null;
    groupEditorData: BrandGroupEditorData | null;
    groupEditorNameDraft: string;
    setGroupEditorNameDraft: (value: string) => void;
    isGroupEditorLoading: boolean;
    groupEditorError: string | null;
    groupEditorPickedBrandNames: string[];
    groupEditorBrandsPage: number;
    setGroupEditorBrandsPage: (page: number) => void;
    groupEditorBrandsOnPage: number;
    onGroupEditorBrandsOnPageUpdate: (count: number) => void;
    isGroupEditorNameSaving: boolean;
    isGroupEditorUngrouping: boolean;
    normalizedGroupEditorNameDraft: string;
    hasGroupEditorNameChanged: boolean;
    groupEditorBrandsTotalRows: number;
    groupEditorBrandsPageCount: number;
    groupEditorPagedBrands: BrandGroupEditorBrand[];
    isGroupEditorHeaderChecked: boolean;
    groupEditorCheckboxData: TableCheckboxData[];
    onGroupEditorClose: () => void;
    onGroupEditorModalVisibilityChange: (isShown: boolean) => void;
    renameGroupEditor: () => Promise<void>;
    onGroupEditorRenameSubmit: (event: FormEvent<HTMLFormElement>) => void;
    onGroupEditorHeaderCheckboxChange: (checked: boolean) => void;
    onGroupEditorCheckboxChange: (checked: boolean, item: BrandGroupEditorBrand) => void;
    onUngroupSelectedBrands: () => Promise<void>;
    groupEditorColumns: TableColumn<BrandGroupEditorBrand>[];
};

export type ProductsTableModalsProps = {
    openedProduct: ProductsTableData | null;
    openedProductAnalysis: ProductAnalysisEntity | null;
    isProductAnalysisLoading: boolean;
    productAnalysisError: string | null;
    onProductAnalysisClose: () => void;
    openedProductAiResponse: ProductsTableData | null;
    openedProductAiData: ProductAiAnalysisEntity | null;
    isProductAiLoading: boolean;
    productAiError: string | null;
    onProductAiClose: () => void;
    openedGroupEditor: ProductsTableData | null;
    groupEditorData: ProductGroupEditorData | null;
    groupEditorNameDraft: string;
    setGroupEditorNameDraft: (value: string) => void;
    isGroupEditorLoading: boolean;
    groupEditorError: string | null;
    groupEditorPickedProductIds: string[];
    groupEditorProductsPage: number;
    setGroupEditorProductsPage: (page: number) => void;
    groupEditorProductsOnPage: number;
    onGroupEditorProductsOnPageUpdate: (count: number) => void;
    isGroupEditorNameSaving: boolean;
    isGroupEditorUngrouping: boolean;
    normalizedGroupEditorNameDraft: string;
    hasGroupEditorNameChanged: boolean;
    groupEditorProductsTotalRows: number;
    groupEditorProductsPageCount: number;
    groupEditorPagedProducts: ProductGroupEditorProduct[];
    isGroupEditorHeaderChecked: boolean;
    groupEditorCheckboxData: TableCheckboxData[];
    onGroupEditorClose: () => void;
    onGroupEditorModalVisibilityChange: (isShown: boolean) => void;
    renameGroupEditor: () => Promise<void>;
    onGroupEditorRenameSubmit: (event: FormEvent<HTMLFormElement>) => void;
    onGroupEditorHeaderCheckboxChange: (checked: boolean) => void;
    onGroupEditorCheckboxChange: (checked: boolean, item: ProductGroupEditorProduct) => void;
    onUngroupSelectedProducts: () => Promise<void>;
    groupEditorColumns: TableColumn<ProductGroupEditorProduct>[];
    openedProductPhotos: {
        photos: ProductPhotoEntity[];
        index: number;
    } | null;
    isProductPhotoLoading: boolean;
    setIsProductPhotoLoading: (value: boolean) => void;
    onProductPhotoPreviewClose: () => void;
    onProductPhotoPreviewPrevious: () => void;
    onProductPhotoPreviewNext: () => void;
};

export const BrandsTableModals = ({
    openedBrandGroup,
    openedBrandGroupAnalysis,
    isBrandGroupAnalysisLoading,
    brandGroupAnalysisError,
    onBrandGroupAnalysisClose,
    openedGroupEditor,
    groupEditorData,
    groupEditorNameDraft,
    setGroupEditorNameDraft,
    isGroupEditorLoading,
    groupEditorError,
    groupEditorPickedBrandNames,
    groupEditorBrandsPage,
    setGroupEditorBrandsPage,
    groupEditorBrandsOnPage,
    onGroupEditorBrandsOnPageUpdate,
    isGroupEditorNameSaving,
    isGroupEditorUngrouping,
    normalizedGroupEditorNameDraft,
    hasGroupEditorNameChanged,
    groupEditorBrandsTotalRows,
    groupEditorBrandsPageCount,
    groupEditorPagedBrands,
    isGroupEditorHeaderChecked,
    groupEditorCheckboxData,
    onGroupEditorClose,
    onGroupEditorModalVisibilityChange,
    renameGroupEditor,
    onGroupEditorRenameSubmit,
    onGroupEditorHeaderCheckboxChange,
    onGroupEditorCheckboxChange,
    onUngroupSelectedBrands,
    groupEditorColumns,
}: BrandsTableModalsProps) => (
    <GroupTableModalsCore
        openedAnalysisTarget={openedBrandGroup}
        analysisData={openedBrandGroupAnalysis}
        isAnalysisLoading={isBrandGroupAnalysisLoading}
        analysisError={brandGroupAnalysisError}
        onAnalysisClose={onBrandGroupAnalysisClose}
        groupEditorProps={{
            openedGroupEditor,
            groupEditorData,
            groupEditorNameDraft,
            setGroupEditorNameDraft,
            isGroupEditorLoading,
            groupEditorError,
            pickedItems: groupEditorPickedBrandNames,
            page: groupEditorBrandsPage,
            setPage: setGroupEditorBrandsPage,
            rowsOnPage: groupEditorBrandsOnPage,
            onRowsOnPageUpdate: onGroupEditorBrandsOnPageUpdate,
            isGroupEditorNameSaving,
            isGroupEditorUngrouping,
            normalizedGroupEditorNameDraft,
            hasGroupEditorNameChanged,
            totalRows: groupEditorBrandsTotalRows,
            pageCount: groupEditorBrandsPageCount,
            pagedItems: groupEditorPagedBrands,
            isGroupEditorHeaderChecked,
            groupEditorCheckboxData,
            onGroupEditorClose,
            onGroupEditorModalVisibilityChange,
            renameGroupEditor,
            onGroupEditorRenameSubmit,
            onGroupEditorHeaderCheckboxChange,
            onGroupEditorCheckboxChange,
            onUngroupSelected: onUngroupSelectedBrands,
            groupEditorColumns,
        }}
        groupEditorTotalLabel="Брендов в группе"
        groupEditorEmptyStateLabel="В группе нет брендов."
    />
);

export const ProductsTableModals = ({
    openedProduct,
    openedProductAnalysis,
    isProductAnalysisLoading,
    productAnalysisError,
    onProductAnalysisClose,
    openedProductAiResponse,
    openedProductAiData,
    isProductAiLoading,
    productAiError,
    onProductAiClose,
    openedGroupEditor,
    groupEditorData,
    groupEditorNameDraft,
    setGroupEditorNameDraft,
    isGroupEditorLoading,
    groupEditorError,
    groupEditorPickedProductIds,
    groupEditorProductsPage,
    setGroupEditorProductsPage,
    groupEditorProductsOnPage,
    onGroupEditorProductsOnPageUpdate,
    isGroupEditorNameSaving,
    isGroupEditorUngrouping,
    normalizedGroupEditorNameDraft,
    hasGroupEditorNameChanged,
    groupEditorProductsTotalRows,
    groupEditorProductsPageCount,
    groupEditorPagedProducts,
    isGroupEditorHeaderChecked,
    groupEditorCheckboxData,
    onGroupEditorClose,
    onGroupEditorModalVisibilityChange,
    renameGroupEditor,
    onGroupEditorRenameSubmit,
    onGroupEditorHeaderCheckboxChange,
    onGroupEditorCheckboxChange,
    onUngroupSelectedProducts,
    groupEditorColumns,
    openedProductPhotos,
    isProductPhotoLoading,
    setIsProductPhotoLoading,
    onProductPhotoPreviewClose,
    onProductPhotoPreviewPrevious,
    onProductPhotoPreviewNext,
}: ProductsTableModalsProps) => {
    const openedProductPhoto = openedProductPhotos?.photos[openedProductPhotos.index];
    const extraModals =
        openedProductAiResponse || openedProductPhotos ? (
            <>
                {openedProductAiResponse && (
                    <ModalProductAiResponseData
                        isShown={true}
                        setIsShown={onProductAiClose}
                        productName={openedProductAiResponse.name}
                        loading={isProductAiLoading}
                        error={productAiError}
                        reviews={openedProductAiData?.reviews || []}
                    />
                )}
                {openedProductPhotos && (
                    <Modal
                        isShown={true}
                        setIsShown={onProductPhotoPreviewClose}
                        hideHeader
                        className="h-[90vh] w-[90vw] max-w-[1000px]"
                    >
                        <div className="relative h-full w-full bg-slate-900 p-4">
                            <button
                                type="button"
                                aria-label="Закрыть просмотр фото"
                                className="absolute right-4 top-4 z-10 h-8 w-8 rounded-full bg-white/90 text-sm text-slate-900 transition-colors hover:bg-white"
                                onClick={onProductPhotoPreviewClose}
                            >
                                x
                            </button>
                            <div className="flex h-full items-center justify-center gap-4">
                                <button
                                    type="button"
                                    aria-label="Предыдущее фото"
                                    className="h-10 w-10 rounded-full bg-white/90 text-xl text-slate-900 transition-colors hover:bg-white disabled:cursor-default disabled:bg-white/40"
                                    onClick={onProductPhotoPreviewPrevious}
                                    disabled={openedProductPhotos.photos.length < 2}
                                >
                                    {'<'}
                                </button>
                                <div className="relative flex h-full w-full items-center justify-center">
                                    {isProductPhotoLoading && (
                                        <div className="absolute inset-0 flex items-center justify-center rounded bg-slate-800/70 text-sm text-slate-200">
                                            Загрузка фото...
                                        </div>
                                    )}
                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                    <img
                                        key={`${openedProductPhotos.index}-${openedProductPhoto?.fullSize || ''}`}
                                        src={openedProductPhoto?.fullSize || ''}
                                        alt={`Фото ${openedProductPhotos.index + 1}`}
                                        className={`max-h-full max-w-full object-contain transition-opacity duration-150 ${
                                            isProductPhotoLoading ? 'opacity-0' : 'opacity-100'
                                        }`}
                                        onLoad={() => setIsProductPhotoLoading(false)}
                                        onError={() => setIsProductPhotoLoading(false)}
                                    />
                                </div>
                                <button
                                    type="button"
                                    aria-label="Следующее фото"
                                    className="h-10 w-10 rounded-full bg-white/90 text-xl text-slate-900 transition-colors hover:bg-white disabled:cursor-default disabled:bg-white/40"
                                    onClick={onProductPhotoPreviewNext}
                                    disabled={openedProductPhotos.photos.length < 2}
                                >
                                    {'>'}
                                </button>
                            </div>
                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded bg-black/70 px-3 py-1 text-sm text-white">
                                {openedProductPhotos.index + 1} / {openedProductPhotos.photos.length}
                            </div>
                        </div>
                    </Modal>
                )}
            </>
        ) : null;

    return (
        <GroupTableModalsCore
            openedAnalysisTarget={openedProduct}
            analysisData={openedProductAnalysis}
            isAnalysisLoading={isProductAnalysisLoading}
            analysisError={productAnalysisError}
            onAnalysisClose={onProductAnalysisClose}
            groupEditorProps={{
                openedGroupEditor,
                groupEditorData,
                groupEditorNameDraft,
                setGroupEditorNameDraft,
                isGroupEditorLoading,
                groupEditorError,
                pickedItems: groupEditorPickedProductIds,
                page: groupEditorProductsPage,
                setPage: setGroupEditorProductsPage,
                rowsOnPage: groupEditorProductsOnPage,
                onRowsOnPageUpdate: onGroupEditorProductsOnPageUpdate,
                isGroupEditorNameSaving,
                isGroupEditorUngrouping,
                normalizedGroupEditorNameDraft,
                hasGroupEditorNameChanged,
                totalRows: groupEditorProductsTotalRows,
                pageCount: groupEditorProductsPageCount,
                pagedItems: groupEditorPagedProducts,
                isGroupEditorHeaderChecked,
                groupEditorCheckboxData,
                onGroupEditorClose,
                onGroupEditorModalVisibilityChange,
                renameGroupEditor,
                onGroupEditorRenameSubmit,
                onGroupEditorHeaderCheckboxChange,
                onGroupEditorCheckboxChange,
                onUngroupSelected: onUngroupSelectedProducts,
                groupEditorColumns,
            }}
            groupEditorTotalLabel="Товаров в группе"
            groupEditorEmptyStateLabel="В группе нет товаров."
            extraModals={extraModals}
        />
    );
};
